import styled, { css } from 'styled-components';
import { Input } from 'antd';
import { themeColor } from '~/common/utils/theme';
import { Typography } from '~/ui/components';

type InputProps = {
  $isDisabled?: boolean;
  $withValue?: boolean;
};

export const InputContainer = styled.div.attrs({
  className: 'mt-input',
})`
  min-height: 56px;
  border: 1px solid ${({ theme }) => theme.colors.divider.primary};
  background-color: ${themeColor('background.primary', { dark: 'background.primary' })};
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  width: 100%;
  &:focus,
  &:focus-within {
    border-color: ${themeColor('brand.primary')};
  }

  &:hover {
    ${({ $isDisabled }) =>
      $isDisabled
        ? css`
            background-color: ${themeColor('element.disabledBackground')};
          `
        : css`
            background-color: ${themeColor('background.secondary', {
              dark: 'background.primary',
            })} !important;
          `}
  }
`;

export const inputCss = css<InputProps>`
  &.multiSelect-custom {
    caret-color: transparent;
  }
  &.ant-input,
  &.ant-input-affix-wrapper input {
    width: 100%;
    font-size: ${({ theme }) => theme.typography.types.paragraphSmall.size};
    line-height: ${({ theme }) => theme.typography.types.paragraphSmall.lineHeight};
    font-family: ${({ theme }) => theme.typography.fonts[theme.typography.defaultFamily]};
    border-color: transparent;
    background-color: transparent;
    transform: translate(${({ prefix }) => (prefix ? '30px' : '0')}, 16px);
    border-radius: 0;

    ${({ $isDisabled }) =>
      $isDisabled
        ? css`
            color: ${({ theme }) => theme.colors.element.disabled};
          `
        : css`
            color: ${({ theme }) => theme.colors.element.primary};
          `}

    &::placeholder {
      color: ${({ theme }) => theme.colors.element.placeholder};
      opacity: 0.6;
    }

    &.ant-input-lg {
      height: 56px;
      padding: 0 8px;
    }

    &:focus,
    &:active,
    &:hover {
      outline: none;
      box-shadow: none;
      border-color: transparent;
    }

    &.ant-input-lg:focus {
      height: 24px;
      transform: translate(${({ prefix }) => (prefix ? '30px' : '0')}, 24px);
    }

    ${({ $withValue }) =>
      $withValue &&
      css`
        &.ant-input-lg {
          height: 24px;
          transform: translate(${({ prefix }) => (prefix ? '30px' : '0')}, 24px);
        }
      `}

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-text-fill-color: ${({ theme }) => theme.colors.element.primary};
      -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.colors.background.primary} inset;
      caret-color: ${({ theme }) => theme.colors.element.primary};
      transition: background-color 5000s ease-in-out 0s;
    }
  }

  &.ant-input-affix-wrapper {
    &:hover .ant-input:not(.ant-input-disabled) {
      outline: none;
      box-shadow: none;
      border-color: transparent;
    }
  }

  .ant-input-suffix {
    top: 28px;
  }

  /* Toggle "mostrar senha": o padrão do antd é preto translúcido, some no tema escuro.
     Usa a cor de texto secundária (e a primária no hover) para ficar sempre visível. */
  .ant-input-password-icon {
    color: ${themeColor('element.secondary')};

    &:hover {
      color: ${themeColor('element.primary')};
    }
  }

  &.allow-clear {
    .ant-input-suffix {
      display: none;
      right: 11px;

      svg {
        background-color: ${themeColor('background.secondary', { dark: 'background.primary' })};
        color: ${themeColor('element.secondary', { dark: 'element.secondary' })};
      }
    }

    &:hover .ant-input-suffix {
      display: block;
    }
  }
`;

type InputLabelProps = {
  withValue?: boolean;
  withIcon?: boolean;
};

export const filledInputLabel = css<InputLabelProps>`
  font-size: ${({ theme }) => theme.typography.types.bodyXSmall.size} !important;
  font-weight: ${({ theme }) => theme.typography.types.bodyXSmall.weight};
  line-height: ${({ theme }) => theme.typography.types.bodyXSmall.lineHeight};
  color: ${({ theme }) => theme.colors.element.secondary};
  transform: translate(${({ withIcon }) => (withIcon ? '38px' : '8px')}, 6px);
`;

export const InputLabel = styled(Typography).attrs({
  element: 'label',
  type: 'bodyMedium',
  color: 'element.secondary',
})<InputLabelProps>`
  position: absolute;
  transform: translate(${({ withIcon }) => (withIcon ? '38px' : '8px')}, 16px);
  z-index: 8;
  transition-property: transform, font-size, color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  cursor: text;

  &:has(+ .ant-input-affix-wrapper .ant-input:focus),
  &:has(+ .ant-input:focus),
  &:-webkit-autofill {
    ${filledInputLabel}
  }

  ${({ withValue }) => withValue && filledInputLabel}
`;

export const StyledInput = styled(Input)<InputProps>`
  ${inputCss}
`;

export const StyledPasswordInput = styled(Input.Password)<InputProps>`
  ${inputCss}
`;

export const StyledTextAreaInput = styled(Input.TextArea)<InputProps>`
  ${inputCss}
`;
